import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentFinancialYear, inr } from '@hrms/shared';
import { api } from '../../../lib/api';
import { errorMessage, fieldErrors } from '../../../lib/errors';
import { DOCUMENT_HINT, pickDocument } from '../../../lib/picker';
import { useMut } from '../../../lib/query';
import { appendFile, type UploadFile } from '../../../lib/upload';
import { useGo } from '../../../navigation/nav';
import { Alert, Button, Card, Columns, DataTable, Loading, PageHeader, Row, Screen, Select, Stack, StatusBadge, Text, TextField, useTheme } from '../../../ui';
import { fyOptions, TAX_SECTIONS } from './tax';

export function TaxProofsScreen() {
  const go = useGo();
  const { colors } = useTheme();
  const [fy, setFy] = useState(currentFinancialYear());
  const q = useQuery({ queryKey: ['tax', 'declaration', fy], queryFn: () => api.tax.myDeclaration(fy) });
  const [section, setSection] = useState('80C');
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState<UploadFile | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [pickErr, setPickErr] = useState<string | null>(null);
  const upload = useMut(
    () => {
      const fd = new FormData();
      fd.append('tax_declaration_id', String(q.data!.id));
      fd.append('section', section);
      fd.append('amount', amount);
      appendFile(fd, 'file', file);
      return api.tax.uploadProof(fd);
    },
    {
      invalidate: [['tax']],
      success: 'Proof uploaded',
      onSuccess: () => {
        setAmount('');
        setFile(null);
        setErrs({});
      },
      onError: (e) => setErrs(fieldErrors(e)),
    },
  );
  if (q.isLoading) return <Loading />;
  const declared = Array.from(new Set((q.data?.items ?? []).map((i) => i.section)));
  const choose = async () => {
    setPickErr(null);
    try {
      const f = await pickDocument();
      if (f) setFile(f);
    } catch (e) {
      setPickErr(errorMessage(e));
    }
  };
  return (
    <Screen>
      <PageHeader title="Investment proofs" actions={<Select value={fy} onChange={setFy} options={fyOptions()} />} />
      <Columns ratio={[2, 3]}>
        <Card title="Upload proof">
          {!q.data ? (
            <Stack gap={10}>
              <Alert tone="warning">Submit a tax declaration first.</Alert>
              <Button title="Go to tax declaration" onPress={() => go('TaxDeclaration')} />
            </Stack>
          ) : (
            <Stack gap={14}>
              <Select label="Section" value={section} onChange={setSection} error={errs.section} options={TAX_SECTIONS.filter((s) => !declared.length || declared.includes(s.key)).map((s) => ({ value: s.key, label: s.label }))} />
              <TextField label="Amount" required keyboardType="numeric" value={amount} onChangeText={(v) => setAmount(v.replace(/[^0-9.]/g, ''))} error={errs.amount} />
              <View style={{ gap: 6 }}>
                <Text variant="label">
                  File <Text color={colors.danger}>*</Text>
                </Text>
                <Row>
                  <Button icon="upload" title={file ? 'Replace file' : 'Choose file'} onPress={choose} />
                  <Text variant="small" numberOfLines={1} style={{ flex: 1 }}>
                    {file?.name ?? DOCUMENT_HINT}
                  </Text>
                </Row>
                {!!errs.file && <Text size={12} color={colors.danger}>{errs.file}</Text>}
              </View>
              {pickErr && <Alert tone="warning">{pickErr}</Alert>}
              <View style={{ alignItems: 'flex-end' }}>
                <Button title="Upload" icon="upload" variant="primary" loading={upload.isPending} disabled={!file || !amount} onPress={() => upload.mutate(undefined)} />
              </View>
            </Stack>
          )}
        </Card>
        <DataTable
          rows={q.data?.proofs ?? []}
          emptyTitle="No proofs uploaded"
          columns={[
            { key: 'section', title: 'Section', width: 90 },
            { key: 'file_name', title: 'File', width: 160 },
            { key: 'amount', title: 'Amount', width: 110, align: 'right', render: (r) => inr(r.amount), sortValue: (r) => r.amount },
            { key: 'status', title: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} />, sortValue: (r) => r.status },
            { key: 'remarks', title: 'Remarks', width: 140 },
          ]}
        />
      </Columns>
    </Screen>
  );
}
